"use client";

// Filterkopf der Bestätigungen: Suche · Jahr · Form, darunter Zustand.
import { useTransition } from "react";
import { useRouter } from "next/navigation";
import { Send } from "lucide-react";
import { Button, toast } from "@/components/ui";
import {
  Filterkopf,
  Filterzurueck,
  Luecke,
  Schiene,
  Suchfeld,
  useAnsicht,
  Zaehlknopf,
  Zaehlschiene,
} from "@/components/liste";
import { BELEG_FORMEN, BELEG_PARAMS, BELEG_ZUSTAENDE, belegFilterAnzahl, type BelegAnsicht } from "@/lib/spenden/ansicht";
import { bestaetigungenVersenden } from "@/app/(app)/spenden/actions";

export function BelegFilterkopf({
  ansicht,
  jahre,
  formen,
  zustaende,
}: {
  ansicht: BelegAnsicht;
  jahre: { jahr: string; anzahl: number }[];
  formen: Record<string, number>;
  zustaende: Record<string, number>;
}) {
  const { setzen, isPending } = useAnsicht();
  return (
    <Filterkopf
      isPending={isPending}
      oben={
        <>
          <Suchfeld wert={ansicht.q} platzhalter="Nummer, Name, Ort …" beschriftung="Bestätigungen durchsuchen" />
          <Luecke />
          <Zaehlschiene titel="Spendenjahr">
            {jahre.map((j) => (
              <Zaehlknopf
                key={j.jahr}
                label={j.jahr}
                anzahl={j.anzahl}
                aktiv={ansicht.jahr === j.jahr}
                onClick={() => setzen({ jahr: ansicht.jahr === j.jahr ? null : j.jahr })}
              />
            ))}
          </Zaehlschiene>
          <Schiene>
            {BELEG_FORMEN.map((f) => (
              <Zaehlknopf
                key={f.schluessel}
                label={f.label}
                anzahl={formen[f.schluessel] ?? 0}
                aktiv={ansicht.form === f.schluessel}
                onClick={() => setzen({ form: ansicht.form === f.schluessel ? null : f.schluessel })}
              />
            ))}
          </Schiene>
        </>
      }
      unten={
        <>
          <Schiene>
            {BELEG_ZUSTAENDE.map((z) => (
              <Zaehlknopf
                key={z.schluessel || "gueltig"}
                label={z.label}
                anzahl={zustaende[z.schluessel] ?? 0}
                aktiv={ansicht.zustand === z.schluessel}
                titel={z.hinweis}
                ton={z.schluessel === "unversendet" ? "gefahr" : "normal"}
                onClick={() => setzen({ zustand: z.schluessel || null })}
              />
            ))}
          </Schiene>
          <Luecke />
          <Filterzurueck anzahl={belegFilterAnzahl(ansicht)} params={BELEG_PARAMS} />
        </>
      }
    />
  );
}

/** Alle ausgestellten, aber nicht versendeten Bestätigungen auf ihrem Weg hinausschicken. */
export function UnversendeteSenden({ ids }: { ids: string[] }) {
  const router = useRouter();
  const [laeuft, starte] = useTransition();
  if (ids.length === 0) return null;
  return (
    <Button
      variant="outline"
      loading={laeuft}
      onClick={() =>
        starte(async () => {
          const r = await bestaetigungenVersenden(ids, "AUTO");
          if (!r.success) toast.error(r.error);
          else {
            toast.success(`${r.anzahl} ${r.anzahl === 1 ? "Bestätigung" : "Bestätigungen"} versendet`, {
              description: r.hinweis ?? "Per Mail, wo eine Adresse hinterlegt ist und Mail gewünscht wird — sonst für den Briefdruck vorgemerkt.",
            });
            router.refresh();
          }
        })
      }
    >
      <Send className="h-4 w-4" aria-hidden />
      {ids.length} unversendete senden
    </Button>
  );
}
